import React from 'react';
import { useNavigate } from 'react-router-dom';
import AppLink from '../shared/AppLink';
import { trackEvent } from '../../utils/analytics';

const CardIcon = ({ name, className = 'h-12 w-12 text-emerald-300' }) => {
  switch (name) {
    case 'listen':
      return (
        <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden>
          <path d="M12 4a7 7 0 00-7 7v2a4 4 0 004 4h1" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
          <path d="M12 4a7 7 0 017 7v2a4 4 0 01-4 4h-1" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
          <circle cx="12" cy="11" r="2.5" stroke="currentColor" strokeWidth="1.6"/>
        </svg>
      );
    case 'bespoke':
      return (
        <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden>
          <path d="M4 7h10M4 12h8M4 17h6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
          <path d="M15.5 4.5l4 4m-6 10.5h6v-6l-6 6z" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      );
    case 'library':
      return (
        <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden>
          <rect x="3.5" y="4" width="6" height="16" rx="1.5" stroke="currentColor" strokeWidth="1.6"/>
          <rect x="10.5" y="4" width="6" height="16" rx="1.5" stroke="currentColor" strokeWidth="1.6"/>
          <path d="M20 5v14" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
        </svg>
      );
    default:
      return null;
  }
};

const ProofIcon = ({ name, className = 'h-6 w-6 text-white' }) => {
  switch (name) {
    case 'restaurants':
      return (
        <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden>
          <path d="M6 3v8M10 3v8M6 7h4M14 3h2a3 3 0 013 3v5h-5V3zM17 11v10" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
        </svg>
      );
    case 'activities':
      return (
        <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden>
          <path d="M4 18c2-2 4-3 8-3s6 1 8 3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
          <circle cx="12" cy="8" r="3" stroke="currentColor" strokeWidth="1.6"/>
        </svg>
      );
    case 'lodging':
      return (
        <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden>
          <path d="M3 11l9-7 9 7v8a2 2 0 01-2 2h-3v-6H8v6H5a2 2 0 01-2-2v-8z" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      );
    case 'regions':
      return (
        <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden>
          <path d="M4 4h16v8H4zM4 16h10v4H4z" stroke="currentColor" strokeWidth="1.6"/>
        </svg>
      );
    case 'areas':
      return (
        <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden>
          <path d="M3 7l6-3 6 3 6-3v10l-6 3-6-3-6 3V7z" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      );
    case 'blog':
      return (
        <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden>
          <path d="M5 4h9l5 5v11a2 2 0 01-2 2H5a2 2 0 01-2-2V6a2 2 0 012-2z" stroke="currentColor" strokeWidth="1.6"/>
          <path d="M14 4v5h5" stroke="currentColor" strokeWidth="1.6"/>
        </svg>
      );
    default:
      return null;
  }
};

const WhyAlakai = ({
  title = "Why Alaka‘i Aloha",
  items = [],
  proof = [],
  cta = true,
  ctaLabel = 'Plan Your Trip',
  onPrimaryCtaClick,
  ctaLocation = 'why_alakai_cta',
}) => {
  const navigate = useNavigate();
  return (
    <section>
      <h2 className="text-3xl md:text-4xl font-semibold text-center mb-6">{title}</h2>
      <div className="max-w-5xl mx-auto px-4 grid md:grid-cols-3 gap-4 md:gap-6">
        {items.map((it, idx) => (
          <div key={idx} className="glass-card rounded-lg p-5">
            {/* Title at the top */}
            <div className="font-semibold text-2xl md:text-2xl mb-3 flex items-center gap-3 tracking-tight">
              {!it.imageSrc && (
                <span className="inline-flex h-12 w-12 items-center justify-center rounded-lg bg-white/5 border border-white/10">
                  <CardIcon name={it.icon} />
                </span>
              )}
              <span>{it.title}</span>
            </div>

            {/* Square image in the middle (if provided) */}
            {it.imageSrc && (
              <div className="overflow-hidden mb-3 -mx-5 border-t border-b border-white/15">
                <img
                  src={it.imageSrc}
                  alt={it.title}
                  loading="lazy"
                  className="w-full h-full object-cover"
                  style={{ aspectRatio: '1 / 1' }}
                />
              </div>
            )}

            {/* Description at the bottom */}
            <p className="text-sm text-white leading-relaxed">{it.body}</p>
          </div>
        ))}
      </div>

      {Array.isArray(proof) && proof.length > 0 && (
        <div className="max-w-5xl mx-auto px-4 mt-6">
          <div className="glass-card rounded-lg p-4 md:p-5">
            <div className="grid grid-cols-3 md:grid-cols-6 gap-3 md:gap-4">
              {proof.map((p, i) => (
                <AppLink key={i} to={p.to} className="group flex flex-col items-center text-center gap-2">
                  <div className="w-20 h-20 md:w-24 md:h-24 rounded-lg overflow-hidden border border-white/10 bg-white/5">
                    {p.imageSrc ? (
                      <img
                        src={p.imageSrc}
                        alt={p.label}
                        loading="lazy"
                        className="w-full h-full object-cover transition-transform duration-200 group-hover:scale-105"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <ProofIcon name={p.icon} className="h-6 w-6 text-emerald-300" />
                      </div>
                    )}
                  </div>
                  <span className="text-xs md:text-sm font-medium text-white/90 group-hover:text-white">
                    {p.label}
                  </span>
                </AppLink>
              ))}
            </div>
          </div>
        </div>
      )}

      {cta && (
        <div className="max-w-5xl mx-auto px-4 mt-6 flex justify-center">
          <button
            type="button"
            className="btn-primary-cta w-full sm:w-auto"
            onClick={() => {
              if (onPrimaryCtaClick) {
                onPrimaryCtaClick(ctaLocation, ctaLabel);
                return;
              }
              trackEvent('interview_started', 'conversion', 'why_alakai_cta');
              navigate('/interview/wizard');
            }}
          >
            {ctaLabel}
          </button>
        </div>
      )}
    </section>
  );
};

export default WhyAlakai;
